import { LoadingOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, Spin, Tree, message } from "antd";
import type { DataNode, TreeProps } from "antd/es/tree";
import { Key, memo, useState } from "react";
import { getEnv } from "../../../../AppConfigs";
import { FontFa } from "../../../../components";
import { useSearchParams } from "../../../../hook/use-search-params";
import { useMovePublicDocDirectory, usePublicDocDirectory, useUpdatePublicDocDirectory } from "../../hooks";
import useMovePublicFolder from "../../hooks/published-documents/use-move-public-folder";
import { generateTitleMoveConfirmModal } from "../../utils/generateTitleMoveConfirmModal";
import "./document-list.module.scss";
import { rootFolderData } from "./mockup-folder";

const getArrContainTargetNode = (info: {
  treeData?: DataNode[];
  targetKey: string | number;
  position: number;
}): DataNode[] | undefined => {
  const { treeData = [], targetKey, position } = info;
  const willGetChildren = position === 0;

  const targetNodeIdx = treeData.findIndex((treeNode) => treeNode.key === targetKey);

  if (targetNodeIdx !== -1) {
    const targetNode = treeData[targetNodeIdx];
    return willGetChildren ? targetNode?.children ?? [] : treeData;
  }

  for (let treeNode of treeData) {
    if (treeNode?.children && treeNode?.children?.length > 0) {
      const result = getArrContainTargetNode({ treeData: treeNode.children, targetKey, position });
      if (result) return result;
    }
  }
  return undefined;
};

const PublicDocumentListDraggable = () => {
  const [loadingState, setLoadingState] = useState(false);
  const [modal, contextHolder] = Modal.useModal();

  const queryClient = useQueryClient();
  const { treeData, isLoading, hashedData, isError } = usePublicDocDirectory();
  const { mutateAsync: moveDocDirectoryAsync } = useMovePublicDocDirectory();
  const { mutateAsync: updateDocDirectoryAsync } = useUpdatePublicDocDirectory();
  const { mutateAsync: movePublicFolderAsync } = useMovePublicFolder();
  const { searchParams, replaceSearchParams } = useSearchParams();

  const folderId = searchParams.get("folderId");
  const isSelected = (key: string) => key === folderId;

  const onDrop: TreeProps["onDrop"] = async (info) => {
    const title = generateTitleMoveConfirmModal(info);
    modal.confirm({
      title: title,
      okText: "Xác nhận",
      cancelText: "Hủy",
      onOk: async () => {
        try {
          setLoadingState(true);
          let mapResult = [];
          let newOrderPosition: number;
          const dropNode = info.node;
          const dragNode = info.dragNode;
          const dropPos = info.node.pos.split("-");
          const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
          const result = getArrContainTargetNode({
            treeData,
            targetKey: dropNode.key,
            position: dropPosition,
          });

          if (result) mapResult = result.map((item) => hashedData.get(item.key));

          if (dropPosition === 0 || dropPosition === -1) {
            const minOrderPosition = Math.min(...mapResult.map((item) => item?.docPosition ?? 1));
            newOrderPosition = mapResult.length === 0 ? 0 : minOrderPosition - 1;
          } else {
            const dropNodeIdx = mapResult.findIndex((item) => item.id === dropNode.key);
            const prevDropNodeArr = mapResult.slice(0, dropNodeIdx + 1);
            const nextDropNodeArr = mapResult.slice(dropNodeIdx + 1, mapResult.length);

            const maxPrevOrderPosition = Math.max(...prevDropNodeArr.map((item) => item?.docPosition ?? 0));
            const minNextOrderPosition = Math.min(...nextDropNodeArr.map((item) => item?.docPosition ?? Infinity));

            const maxPrev = prevDropNodeArr.length === 0 ? 0 : maxPrevOrderPosition;
            const minNext = nextDropNodeArr.length === 0 ? maxPrev + 1 : minNextOrderPosition;

            if (nextDropNodeArr.length === 0 || !isFinite(minNextOrderPosition)) {
              newOrderPosition = Math.max(...mapResult.map((item) => item?.docPosition ?? 1)) + 1;
            } else {
              newOrderPosition = (maxPrev + minNext) / 2;
            }
          }

          const rawDragNode = hashedData.get(dragNode.key);
          const rawDropNode = hashedData.get(dropNode.key);
          let parentReferenceId;

          const isDiffBranch = rawDragNode?.parentReference?.id !== rawDropNode?.parentReference?.id;
          if ((isDiffBranch && dropPosition !== 0) || dropPosition === 0) {
            // call api move folder
            parentReferenceId = dropPosition === 0 ? rawDropNode?.id : rawDropNode?.parentReference?.id;
            const body = {
              itemId: rawDragNode.id,
              name: rawDragNode.name,
              parentReference: { id: parentReferenceId },
            };

            await moveDocDirectoryAsync(body);
          }
          // update order position
          await updateDocDirectoryAsync({ itemId: rawDragNode.listId, docPosition: newOrderPosition });
          await queryClient.invalidateQueries({ queryKey: ["publish-folders"] });
          await movePublicFolderAsync({
            key: rawDragNode.id,
            newParentKey:
              parentReferenceId === getEnv("REACT_APP_DOCUMENT_FOLDER_ROOT_ID") ? undefined : parentReferenceId,
            newPosition: newOrderPosition ?? undefined,
          });

          await queryClient.invalidateQueries({ queryKey: ["publish-folders-new"], type: "all" });

          message.success("Di chuyển folder thành công");
        } catch (error) {
          console.error(error);
          message.success("Di chuyển folder thất bại");
        } finally {
          setLoadingState(false);
        }
      },
    });
  };

  const handleSelect = (keys: Key[]) => {
    if (keys.length === 0) return;
    searchParams.set("folderId", keys?.[0]?.toString());
    replaceSearchParams(searchParams);
  };

  const spinning = isLoading || loadingState;

  if (isError) return <div>Error...</div>;

  return (
    <Spin spinning={spinning} indicator={<LoadingOutlined rev="" style={{ fontSize: 48 }} spin />}>
      {contextHolder}

      <Tree
        autoExpandParent
        defaultExpandedKeys={["01DXZKD2A2M4AZRRALJJ2J54SKIOV4YQ42"]}
        defaultExpandParent
        defaultSelectedKeys={folderId ? [folderId] : []}
        selectedKeys={folderId ? [folderId] : []}
        draggable={{ icon: false }}
        blockNode
        onDrop={onDrop}
        treeData={spinning ? rootFolderData : treeData}
        titleRender={(node) => (
          <div className="column-folder" style={{ fontWeight: isSelected(node.key.toString()) ? "bold" : "normal" }}>
            <div style={{ display: "inline-block", marginLeft: "8px" }} />
            <span className="mr-1 text-warning">
              <FontFa name="faFolder" />
            </span>
            <span>{node.title}</span>
          </div>
        )}
        onSelect={handleSelect}
      />
    </Spin>
  );
};

export default memo(PublicDocumentListDraggable);
